import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { IconButton, MenuItem, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ConfirmationModal } from '../../../modals';
import AccessConfirmation from './AccessConfirmation';
import InitCometeClockingModal from '../../../features/cometeOnTime/InitCometeClockingModal';
import MenuPopover from '../../../organisms/MenuPopover';
import { MIcon } from '../../../@material-extend';

import useAuth from '../../../../hooks/useAuth';
import { useDispatch, useSelector } from '../../../../redux/store';
import { verifyAccess } from '../../../../redux/slices/clockings';
import { PATH_AUTH, PATH_PAGE } from '../../../../routes/paths';
import CloseButton from '../../../features/clockings/ClockingButtons/ClockingButton/CloseButton';

const HamburgerMenu = () => {
  const anchorRef = useRef(null);
  const navigate = useNavigate();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { clockingDeviceSites } = useSelector((state) => state.clockings); // clockingDeviceSite
  const dispatch = useDispatch();
  const {
    isAuthenticated,
    login,
    logout,
    features: { clockingsByNfc, showOntimeShiftsList }
  } = useAuth();
  const [openMenu, setOpenMenu] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openModalInit, setOpenModalInit] = useState(false);
  const [isDisconnect, setIsDisconnect] = useState(false);
  const [restrictedAction, setRestrictedAction] = useState(null);
  const theme = useTheme();

  const LoginSchema = Yup.object().shape({
    email: Yup.string().default('').required("L'e-mail est requis"),
    password: Yup.string().required('Mot de passe requis')
  });

  const {
    getValues,
    control,
    register,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      email: '',
      password: ''
    },
    resolver: yupResolver(LoginSchema)
  });

  const handleSubmit = (isDisconnect) => {
    const values = getValues();
    dispatch(
      verifyAccess({ email: values.email, password: values.password }, (result) => {
        if (result.success) {
          if (!isDisconnect) {
            login({ ...values, redirect: false });

            switch (restrictedAction) {
              case 'settings':
                navigate(PATH_AUTH.clockingApp.settings, { state: { edit: true } });
                break;

              case 'sites':
              default:
                navigate(PATH_PAGE.clockingApp.sitesManagement);
                break;
            }
          } else {
            handleDisconnect(true);
          }
          reset();
          setOpenModal(false);
        } else {
          return enqueueSnackbar(result.message, {
            variant: 'error',
            action: (key) => <CloseButton handleClick={() => closeSnackbar(key)} />
          });
        }
      })
    );
  };

  const handleDisconnect = (isNavigate) => {
    logout(isNavigate);
  };

  const handleOpenMenu = () => {
    reset();
    setOpenMenu(true);
  };

  const handleClickCode = () => {
    setOpenModalInit(true);
    setOpenMenu(false);
  };

  const handleClickSettings = () => {
    handleCloseMenu(true);
    setRestrictedAction('settings');
  };

  const handleClickChangeSites = () => {
    handleCloseMenu(true);
    setRestrictedAction('sites');
  };

  const handleCloseMenu = (openModal = false, isDisconnect = false) => {
    setOpenMenu(false);
    if (openModal === true) {
      setOpenModal(true);
    }
    setIsDisconnect(isDisconnect);
  };

  return (
    <>
      <ConfirmationModal
        title="Accès manager"
        confirmText="Confirmer"
        open={openModal}
        handleConfirm={() => handleSubmit(isDisconnect)}
        handleClose={() => {
          setOpenModal(false);
          reset();
        }}
      >
        <AccessConfirmation
          control={control}
          errors={errors}
          isDisconnect={!!isDisconnect}
          email={LoginSchema.email}
          register={register}
          setValue={setValue}
          handleSubmit={handleSubmit}
        />
      </ConfirmationModal>

      <InitCometeClockingModal showModal={openModalInit} onClose={() => setOpenModalInit(false)} />

      <IconButton ref={anchorRef} size="large" color="inherit" onClick={handleOpenMenu} data-cy="hamburger-button">
        <MIcon icon="menu" variant="white" />
      </IconButton>

      <MenuPopover
        data-cy="hamburger-menu"
        anchorEl={anchorRef.current}
        open={openMenu}
        sx={{ width: 'auto' }}
        onClose={handleCloseMenu}
      >
        {isAuthenticated && clockingDeviceSites.length > 0 && (
          <MenuItem sx={styleMenuItem} onClick={handleClickCode}>
            <Typography whiteSpace="nowrap">Code d'association</Typography>
          </MenuItem>
        )}

        {isAuthenticated && clockingDeviceSites.length > 0 && (
          <MenuItem sx={styleMenuItem} onClick={handleClickChangeSites}>
            <Typography whiteSpace="nowrap">
              {clockingDeviceSites.length > 1 ? 'Gérer les sites associés' : 'Modifier le site associé'}
            </Typography>
          </MenuItem>
        )}

        {isAuthenticated && clockingsByNfc && showOntimeShiftsList && clockingDeviceSites.length > 0 && (
          <MenuItem sx={styleMenuItem} onClick={handleClickSettings}>
            <Typography whiteSpace="nowrap">Paramètres</Typography>
          </MenuItem>
        )}

        <MenuItem sx={styleMenuItem} onClick={() => handleCloseMenu(true, true)}>
          <Typography sx={{ color: theme.palette.error.main }}>Fermer la session</Typography>
        </MenuItem>
      </MenuPopover>
    </>
  );
};

const styleMenuItem = {
  typography: 'body2',
  py: 1,
  pl: 2.5,
  pr: 4
};

export default HamburgerMenu;
